import { inject, Injectable } from '@angular/core';
import { GenericError } from '@models/classes/generic.error';
import { ErrorCode } from '@models/enums/error.codes';
import { DbConnectOptions, SqLiteQueryWithParams } from '@models/types/sqlite.type';
import { ActionTarget, WorkerPostMessage, WorkerResponseMessage } from '@models/types/worker-msg.type';
import { FileSystemService } from '@services/file-system/file-system.service';
import { v4 as uuidv4 } from 'uuid';
@Injectable({
    providedIn: 'root',
})
export class SqliteService {
    #fileSystemSvc = inject(FileSystemService);

    #workerInstance = new Worker(new URL('../../worker/sqlite.worker', import.meta.url), {
        type: 'module',
    });

    constructor() {}

    /**
     * Get connected database name
     * @returns Promise<string | null> - The name of the connected database
     */
    connectedDBName(actionTarget: ActionTarget = 'normal'): Promise<string | null> {
        return new Promise((resolve, reject) => {
            const controller = new AbortController();
            const signal = controller.signal;
            const token = uuidv4();
            this.#workerInstance.addEventListener(
                'message',
                ({ data }: { data: WorkerResponseMessage<string | null> }) => {
                    if (data.action === 'connectedDBName' && token === data.token) {
                        resolve(data.data);
                        controller.abort();
                    }
                },
                { signal }
            );

            this.#workerInstance.addEventListener(
                'error',
                (error) => {
                    reject(error);
                },
                { once: true }
            );

            const options: WorkerPostMessage<null> = {
                action: 'connectedDBName',
                data: null,
                token,
                actionTarget,
            };
            this.#workerInstance.postMessage(options);
        });
    }

    /**
     * Delete database
     * @param dbName database name
     * @param force force delete the database by disconnecting first (default is false)
     * @returns returns a promise that resolves to void
     */
    async deleteDB(dbName: string, actionTarget: ActionTarget = 'normal', force: boolean = false): Promise<void> {
        const activeDB = await this.connectedDBName(actionTarget);
        if (activeDB === dbName) {
            if (!force) {
                const err = new GenericError();
                err.message = 'Cannot delete active database';
                err.location = 'sqlite.service>deleteDB()';
                throw err;
            }
            await this.closeConnection(actionTarget);
        }

        const dbDir = await this.#fileSystemSvc.getDatabaseDirectory();
        const file = await dbDir.getFileHandle(dbName, { create: false }).catch((err) => {
            if (err.name === 'NotFoundError') {
                return null;
            } else {
                throw err;
            }
        });

        if (file) {
            await dbDir.removeEntry(dbName);
        }
    }

    /**
     * Close database connection
     * @returns Promise<void> - Close the connection to the database
     */
    closeConnection(actionTarget: ActionTarget = 'normal'): Promise<void> {
        const controller = new AbortController();
        const signal = controller.signal;

        return new Promise((resolve, reject) => {
            const token = uuidv4();
            this.#workerInstance.addEventListener(
                'message',
                ({ data }: { data: WorkerResponseMessage<void> }) => {
                    if (data.action === 'close' && token === data.token) {
                        resolve();
                        controller.abort();
                    }
                },
                { signal }
            );

            this.#workerInstance.addEventListener(
                'error',
                (error) => {
                    reject(error);
                },
                { once: true, signal }
            );

            const options: WorkerPostMessage<null> = {
                action: 'close',
                actionTarget,
                data: null,
                token,
            };
            this.#workerInstance.postMessage(options);
        });
    }

    /**
     * returns true if the database exists
     * @param dbName databse name
     * @returns Promise resolve to true if the database exists, false otherwise
     */
    async dbExists(dbName: string): Promise<boolean> {
        try {
            const dbDir = await this.#fileSystemSvc.getDatabaseDirectory();
            const file = await dbDir.getFileHandle(dbName, { create: false });
            return !!file;
        } catch (err) {
            return false;
        }
    }

    /**
     * Connect to a database
     * @param dbName database name
     * @param create create the database if it does not exist (default is false)
     * @returns resolve to true if the connection is successful, false otherwise
     */
    connectDB(dbName: string, actionTarget: ActionTarget = 'normal', create: boolean = false): Promise<boolean> {
        const controller = new AbortController();
        const signal = controller.signal;

        return new Promise((resolve, reject) => {
            const token = uuidv4();

            const options: WorkerPostMessage<DbConnectOptions> = {
                action: 'connect',
                actionTarget,
                data: {
                    dbName,
                    createIfNotExists: create,
                },
                token,
            };
            this.#workerInstance.addEventListener(
                'message',
                ({ data }: { data: WorkerResponseMessage<boolean> }) => {
                    if (data.action === 'connect' && data.token === token) {
                        if (data.responseType === 'failed') {
                            reject(
                                new GenericError({
                                    message: typeof data.error === 'string' ? data.error : 'Error in DB connection',
                                    location: 'sqlite.service>connectDB',
                                    errorObject: data.error,
                                    errorCode: ErrorCode.DB_CONNECTION_FAILED,
                                })
                            );
                            controller.abort();
                            return;
                        }
                        resolve(data.data);
                        controller.abort();
                    }
                },
                {
                    signal,
                }
            );

            this.#workerInstance.addEventListener(
                'error',
                (error) => {
                    reject(error);
                },
                {
                    once: true,
                    signal,
                }
            );
            this.#workerInstance.postMessage(options);
        });
    }

    /**
     * Execute an SQL query
     * @param query Parametarized SQL query
     * @returns Returns a promise that resolves to the result of the query
     */
    runQuery<R>(query: SqLiteQueryWithParams, actionTarget?: ActionTarget): Promise<R>;
    /**
     * Execute an SQL query
     * @param query SQL query
     * @returns Returns a promise that resolves to the result of the query
     */
    runQuery<R>(query: string, actionTarget?: ActionTarget): Promise<R>;
    runQuery<R>(query: string | SqLiteQueryWithParams, actionTarget: ActionTarget = 'normal'): Promise<R> {
        const controller = new AbortController();
        const signal = controller.signal;

        return new Promise((resolve, reject) => {
            const token = uuidv4();
            const options: WorkerPostMessage<string | SqLiteQueryWithParams> = {
                action: 'fetch',
                actionTarget,
                data: query,
                token,
            };

            this.#workerInstance.addEventListener(
                'message',
                ({ data }: { data: WorkerResponseMessage<R> }) => {
                    if (data.action === 'fetch' && token === data.token) {
                        if (data.responseType === 'failed') {
                            reject(
                                new GenericError({
                                    message: typeof data.error?.message === 'string' ? data.error.message : 'Error in SQL query execution',
                                    location: 'sqlite.service>runQuery()',
                                    errorObject: data.error,
                                    errorCode: ErrorCode.SQL_EXEC,
                                })
                            );
                            controller.abort();
                            return;
                        }
                        resolve(data.data);
                        controller.abort();
                    }
                },
                {
                    signal,
                }
            );

            this.#workerInstance.addEventListener(
                'error',
                (error) => {
                    reject(error);
                },
                {
                    once: true,
                    signal,
                }
            );

            this.#workerInstance.postMessage(options);
        });
    }
}
